import type { Element, ElementContent, Root } from 'hast';
import { SKIP, visit } from 'unist-util-visit';

/*
 * A table squeezes its columns to their narrowest before it overflows its scrolling wrapper, and the browser may break a
 * word after punctuation inside it, so a date or a case number would wrap inside itself ("2026-/08-/19", "T003-/0052").
 * In a table cell, every run of non-space characters holding punctuation goes on one line. Plain words still wrap
 * between one another, and a table too wide for the bubble scrolls sideways.
 */
const MAY_BREAK_INSIDE = /[^\p{L}\p{N}\p{M}]\S/u;

const keepWhole = (value: string): ElementContent[] | null => {
    const parts: ElementContent[] = [];
    let last = 0;
    for (const token of value.matchAll(/\S+/g)) {
        if (!MAY_BREAK_INSIDE.test(token[0])) {
            continue;
        }
        if (token.index > last) {
            parts.push({ type: 'text', value: value.slice(last, token.index) });
        }
        parts.push({
            type: 'element',
            tagName: 'span',
            properties: { className: ['whitespace-nowrap'] },
            children: [{ type: 'text', value: token[0] }],
        });
        last = token.index + token[0].length;
    }
    if (!parts.length) {
        return null;
    }
    if (last < value.length) {
        parts.push({ type: 'text', value: value.slice(last) });
    }
    return parts;
};

export const keepTableTokensWhole = () => (tree: Root) => {
    visit(tree, 'element', (cell) => {
        if (cell.tagName !== 'td' && cell.tagName !== 'th') {
            return;
        }
        visit(cell, 'text', (text, index, parent) => {
            const parts = keepWhole(text.value);
            if (!parts || !parent || index === undefined) {
                return;
            }
            (parent as Element).children.splice(index, 1, ...parts);
            return [SKIP, index + parts.length];
        });
        return SKIP;
    });
};
